import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import {
  Typography,
  Input,
  InputNumber,
  Select,
  Button,
  Card,
  Space,
  Alert,
  Row,
  Col,
  Divider,
  Modal,
} from 'antd';
import { ReloadOutlined, ClearOutlined, DollarOutlined, SwapOutlined, PartitionOutlined } from '@ant-design/icons';
import './DeckPricePage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const MOBILE_BREAKPOINT_PX = 768;
const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * Parse deck list text (e.g. "4xOP13-016\n1xOP13-002") into entries.
 */
function parseDeckList(text) {
  if (!text || typeof text !== 'string') return [];
  const normalized = text
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const regex = /(\d+)\s*x\s*([A-Z0-9]+-\d+)/gi;
  const matches = [...normalized.matchAll(regex)];
  const entries = [];
  for (const m of matches) {
    const quantity = parseInt(m[1], 10);
    const cardNumber = m[2].toUpperCase();
    if (quantity > 0 && cardNumber) {
      entries.push({ cardNumber, quantity });
    }
  }
  return entries;
}

/**
 * Get the displayed card (single variant or split across variants) and its line total.
 * quantitySplit: { productId: quantity } where sum(quantity) === card.quantity
 */
function getDisplayedCard(card, selectedProductId, quantitySplit, options) {
  const opts = options || card.options || [];
  if (!opts.length) return { ...card, lineTotal: card.marketPrice * card.quantity, splitBreakdown: null };

  const totalQty = card.quantity;
  const split = quantitySplit && typeof quantitySplit === 'object' ? quantitySplit : null;
  const splitSum = split ? Object.values(split).reduce((a, b) => a + (b || 0), 0) : 0;
  const hasValidSplit = split && splitSum === totalQty && Object.keys(split).length > 0;

  if (hasValidSplit) {
    let lineTotal = 0;
    const splitBreakdown = [];
    for (const [pidStr, qty] of Object.entries(split)) {
      const n = Number(qty) || 0;
      if (n <= 0) continue;
      const pid = Number(pidStr);
      const option = opts.find((o) => o.productId === pid);
      if (!option) continue;
      const price = Number(option.marketPrice) || 0;
      lineTotal += n * price;
      splitBreakdown.push({ option, quantity: n });
    }
    const first = splitBreakdown[0]?.option;
    return {
      ...card,
      quantity: totalQty,
      cardNumber: card.cardNumber,
      productId: first?.productId ?? card.productId,
      name: first?.name ?? card.name,
      setName: first?.setName ?? card.setName,
      imageUrl: first?.imageUrl ?? card.imageUrl,
      productUrl: first?.productUrl ?? card.productUrl,
      marketPrice: first?.marketPrice ?? card.marketPrice,
      lineTotal: Math.round(lineTotal * 100) / 100,
      splitBreakdown: splitBreakdown.length ? splitBreakdown : null,
    };
  }

  const selected = selectedProductId
    ? opts.find((o) => o.productId === selectedProductId)
    : opts.find((o) => o.productId === card.productId);
  const option = selected || opts[0];
  const price = option.marketPrice ?? card.marketPrice;
  const lineTotal = price * totalQty;
  return {
    ...card,
    ...option,
    quantity: totalQty,
    cardNumber: card.cardNumber,
    lineTotal: Math.round(lineTotal * 100) / 100,
    splitBreakdown: null,
  };
}

export default function DeckPricePage() {
  const [pasteText, setPasteText] = useState('');
  const [preference, setPreference] = useState('lowest');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set()); // Set of "cardKey-productId"
  const [selectedProductIds, setSelectedProductIds] = useState({});
  const [variantModal, setVariantModal] = useState(null); // { cardKey, cardName, options, currentProductId }
  const [failedVariantImages, setFailedVariantImages] = useState(new Set()); // productIds that failed in modal
  const [quantitySplit, setQuantitySplit] = useState({}); // { cardKey: { productId: quantity } }
  const [splitModal, setSplitModal] = useState(null); // { cardKey, cardName, options, totalQty, split: { productId: qty } }
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT_PX
  );

  useEffect(() => {
    const check = () => setIsNarrow(window.innerWidth < MOBILE_BREAKPOINT_PX);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const linkProps = isNarrow ? {} : { target: '_blank', rel: 'noopener noreferrer' };

  const handleGetPrices = async () => {
    const entries = parseDeckList(pasteText);
    if (entries.length === 0) {
      setError('No valid deck entries found. Use format like: 4xOP13-016, 1xOP13-002');
      setResult(null);
      return;
    }
    setError(null);
    setLoading(true);
    setResult(null);
    setSelectedProductIds({});
    setQuantitySplit({});
    setImageErrors(new Set());
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/deck-price`, {
        entries,
        preference,
      });
      setResult(data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to fetch prices';
      setError(Array.isArray(msg) ? msg.join(' ') : String(msg));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setPasteText('');
    setError(null);
    setResult(null);
    setSelectedProductIds({});
    setQuantitySplit({});
    setImageErrors(new Set());
  };

  const setCardVariant = (cardKey, productId) => {
    setSelectedProductIds((prev) => ({ ...prev, [cardKey]: productId }));
    setQuantitySplit((prev) => {
      const next = { ...prev };
      delete next[cardKey];
      return next;
    });
    setVariantModal(null);
  };

  const openSplitModal = (cardKey, cardName, options, totalQty, currentProductId) => {
    const existing = quantitySplit[cardKey];
    const initial = existing && Object.keys(existing).length > 0
      ? { ...existing }
      : { [currentProductId]: totalQty };
    setSplitModal({ cardKey, cardName, options, totalQty, split: initial });
  };

  const saveSplit = () => {
    if (!splitModal) return;
    const { cardKey, totalQty, split } = splitModal;
    const sum = Object.values(split).reduce((a, b) => a + (Number(b) || 0), 0);
    if (sum !== totalQty) return; // validation
    const cleaned = {};
    for (const [pid, qty] of Object.entries(split)) {
      const n = Math.max(0, Math.floor(Number(qty) || 0));
      if (n > 0) cleaned[pid] = n;
    }
    if (Object.values(cleaned).reduce((a, b) => a + b, 0) !== totalQty) return;
    setQuantitySplit((prev) => ({ ...prev, [cardKey]: cleaned }));
    setSplitModal(null);
  };

  const openVariantModal = (cardKey, cardName, options, currentProductId) => {
    setFailedVariantImages(new Set());
    setVariantModal({ cardKey, cardName, options, currentProductId });
  };

  /** Image URL to show for a card: use fallback from another variant if this one failed. */
  const getCardImageUrl = (imageKey, displayedCard, options) => {
    const key = `${imageKey}-${displayedCard.productId}`;
    if (!imageErrors.has(key)) return displayedCard.imageUrl;
    const fallback = (options || []).find((o) => !imageErrors.has(`${imageKey}-${o.productId}`));
    return fallback?.imageUrl ?? displayedCard.imageUrl;
  };

  /** In modal: image URL and whose URL it is (for onError). Use fallback from another variant if this one failed. */
  const getModalOptionImage = (opt, options) => {
    if (!failedVariantImages.has(opt.productId)) {
      return { url: opt.imageUrl, productIdForError: opt.productId };
    }
    const fallback = (options || []).find((o) => !failedVariantImages.has(o.productId));
    if (fallback) {
      return { url: fallback.imageUrl, productIdForError: fallback.productId };
    }
    return { url: opt.imageUrl, productIdForError: opt.productId };
  };

  const displayedCards = useMemo(() => {
    if (!result?.cards) return [];
    return result.cards.map((card, i) => {
      const key = `${card.cardNumber}-${i}`;
      const options = card.options || [];
      const currentProductId = selectedProductIds[key] ?? card.productId;
      return getDisplayedCard(card, currentProductId, quantitySplit[key], options);
    });
  }, [result?.cards, selectedProductIds, quantitySplit]);

  const totalPrice = useMemo(() => {
    return displayedCards.reduce((sum, c) => sum + (c.lineTotal ?? 0), 0);
  }, [displayedCards]);

  return (
    <div className="deck-price-section">
      <Title level={3}>Deck Price Calculator</Title>
      <Text type="secondary">
        Paste your deck list (format: <Text code>4xOP13-016</Text> or{' '}
        <Text code>1 x OP13-002</Text>, one per line or space-separated).
      </Text>

      <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 16 }}>
        <Space wrap align="center">
          <Text>Import preference:</Text>
          <Select
            value={preference}
            onChange={setPreference}
            options={[
              { value: 'lowest', label: 'Lowest Price' },
              { value: 'bling', label: 'Bling (Highest)' },
            ]}
            style={{ minWidth: 140 }}
            disabled={loading}
          />
        </Space>

        <TextArea
          placeholder="4xOP13-016&#10;1xOP13-002&#10;..."
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          disabled={loading}
          rows={6}
          style={{ fontFamily: 'ui-monospace, monospace' }}
        />

        <Space className="deck-price-actions" wrap size="middle">
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={handleGetPrices}
            loading={loading}
            className="deck-price-btn-get"
          >
            Get prices
          </Button>
          <Button icon={<ClearOutlined />} onClick={handleClear} disabled={loading} className="deck-price-btn-clear">
            Clear
          </Button>
        </Space>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}

        {result && (
          <>
            {result.notFound?.length > 0 && (
              <Alert
                message="Not found"
                description={`Card(s) not in mapping: ${result.notFound.join(', ')}`}
                type="warning"
                showIcon
              />
            )}

            <div className="deck-price-total deck-price-total-top">
              <Text className="deck-price-total-label">Total:</Text>{' '}
              <Text className="deck-price-total-value">
                ${Number(totalPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Text>
            </div>

            <Row gutter={[16, 16]}>
              {displayedCards.map((card, i) => {
                const originalCard = result.cards[i];
                const imageKey = `${card.cardNumber}-${i}`;
                const options = originalCard?.options || [];
                const hasVariants = options.length > 1;
                const imageUrlToTry = getCardImageUrl(imageKey, card, options);
                const thisImageFailed = imageErrors.has(`${imageKey}-${card.productId}`);
                const allImagesFailed = options.length
                  ? options.every((o) => imageErrors.has(`${imageKey}-${o.productId}`))
                  : thisImageFailed;
                const hasSplit = card.splitBreakdown && card.splitBreakdown.length > 0;
                const showSplitVisual = hasSplit && card.splitBreakdown.length > 1;

                return (
                  <Col xs={24} sm={24} md={12} lg={8} key={imageKey}>
                    <Card size="small" className="deck-price-ant-card">
                      <div className="deck-price-card-inner">
                        <div
                          className={
                            showSplitVisual
                              ? 'deck-price-card-image-wrap deck-price-card-image-wrap-split'
                              : 'deck-price-card-image-wrap'
                          }
                        >
                          {showSplitVisual ? (
                            <div className="deck-price-split-visual deck-price-split-visual-in-card">
                              {card.splitBreakdown.map(({ option, quantity }) => (
                                <div key={option.productId} className="deck-price-split-visual-item">
                                  <a
                                    href={option.productUrl}
                                    {...linkProps}
                                    className="deck-price-split-visual-image-wrap"
                                  >
                                    <img
                                      src={option.imageUrl}
                                      alt={option.setName}
                                      className="deck-price-split-visual-image"
                                      referrerPolicy="no-referrer"
                                    />
                                  </a>
                                  <Text className="deck-price-split-visual-qty">
                                    {quantity}× ${Number(option.marketPrice).toFixed(2)}
                                  </Text>
                                </div>
                              ))}
                            </div>
                          ) : allImagesFailed ? (
                            <a
                              href={card.productUrl}
                              {...linkProps}
                              className="deck-price-card-image-placeholder deck-price-card-image-placeholder-link"
                            >
                              No image — View on TCGPlayer
                            </a>
                          ) : (
                            <a
                              href={card.productUrl}
                              {...linkProps}
                              className="deck-price-card-image-link"
                            >
                              <img
                                src={imageUrlToTry}
                                alt={card.name}
                                className="deck-price-card-image"
                                referrerPolicy="no-referrer"
                                onError={() =>
                                  setImageErrors((prev) =>
                                    new Set(prev).add(`${imageKey}-${card.productId}`)
                                  )
                                }
                              />
                            </a>
                          )}
                        </div>
                        <div className="deck-price-card-body">
                          <Text strong className="deck-price-card-name">
                            {card.name}
                          </Text>
                          <Text type="secondary" className="deck-price-card-set">
                            {card.setName} ({card.cardNumber})
                          </Text>
                          <Text className="deck-price-card-qty">Qty: {card.quantity}</Text>
                          {hasSplit ? (
                            <div className="deck-price-card-split">
                              {showSplitVisual ? (
                                <Text className="deck-price-card-price deck-price-split-total">
                                  Total: ${Number(card.lineTotal).toFixed(2)}
                                </Text>
                              ) : (
                                <Text className="deck-price-card-price">
                                  ${Number(card.marketPrice).toFixed(2)} × {card.quantity} = $
                                  {Number(card.lineTotal).toFixed(2)}
                                </Text>
                              )}
                              {hasVariants && (
                                <Space direction="vertical" size={0}>
                                  {!showSplitVisual && (
                                    <Button
                                      type="link"
                                      size="small"
                                      icon={<SwapOutlined />}
                                      className="deck-price-variant-btn"
                                      onClick={() =>
                                        openVariantModal(
                                          imageKey,
                                          card.name,
                                          options,
                                          selectedProductIds[imageKey] ?? originalCard?.productId
                                        )
                                      }
                                    >
                                      Switch variant
                                    </Button>
                                  )}
                                  <Button
                                    type="link"
                                    size="small"
                                    icon={<PartitionOutlined />}
                                    className="deck-price-variant-btn"
                                    onClick={() =>
                                      openSplitModal(
                                        imageKey,
                                        card.name,
                                        options,
                                        card.quantity,
                                        selectedProductIds[imageKey] ?? originalCard?.productId
                                      )
                                    }
                                  >
                                    Edit split
                                  </Button>
                                </Space>
                              )}
                            </div>
                          ) : (
                            <>
                              <Text className="deck-price-card-price">
                                ${Number(card.marketPrice).toFixed(2)} × {card.quantity} = $
                                {Number(card.lineTotal).toFixed(2)}
                              </Text>
                              {hasVariants && (
                                <Space direction="vertical" size={0}>
                                  <Button
                                    type="link"
                                    size="small"
                                    icon={<SwapOutlined />}
                                    className="deck-price-variant-btn"
                                    onClick={() =>
                                      openVariantModal(
                                        imageKey,
                                        card.name,
                                        options,
                                        selectedProductIds[imageKey] ?? originalCard?.productId
                                      )
                                    }
                                  >
                                    Switch variant
                                  </Button>
                                  <Button
                                    type="link"
                                    size="small"
                                    icon={<PartitionOutlined />}
                                    className="deck-price-variant-btn"
                                    onClick={() =>
                                      openSplitModal(
                                        imageKey,
                                        card.name,
                                        options,
                                        card.quantity,
                                        selectedProductIds[imageKey] ?? originalCard?.productId
                                      )
                                    }
                                  >
                                    Split quantities
                                  </Button>
                                </Space>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </>
        )}

        {/* Split quantities modal */}
        <Modal
          className="deck-price-modal-mobile"
          title={
            splitModal ? (
              <>
                Split quantities: <Text strong>{splitModal.cardName}</Text> (total: {splitModal.totalQty})
              </>
            ) : null
          }
          open={!!splitModal}
          onCancel={() => setSplitModal(null)}
          onOk={saveSplit}
          okText="Save"
          okButtonProps={{
            disabled: splitModal
              ? Object.values(splitModal.split).reduce((a, b) => a + (Number(b) || 0), 0) !== splitModal.totalQty
              : true,
          }}
          width={Math.min(560, typeof window !== 'undefined' ? window.innerWidth - 48 : 560)}
          destroyOnClose
          styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
        >
          {splitModal && (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Text type="secondary">
                Assign how many copies of each variant you have. Sum must equal {splitModal.totalQty}.
              </Text>
              {splitModal.options.map((opt) => (
                <div key={opt.productId} className="deck-price-split-row">
                  <div className="deck-price-split-option-image-wrap">
                    <img
                      src={opt.imageUrl}
                      alt={opt.setName}
                      className="deck-price-split-option-image"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                  <div className="deck-price-split-option-info">
                    <Text strong>{opt.setName}</Text>
                    <Text type="secondary"> — ${Number(opt.marketPrice).toFixed(2)}</Text>
                  </div>
                  <InputNumber
                    min={0}
                    max={splitModal.totalQty}
                    value={splitModal.split[opt.productId] ?? 0}
                    onChange={(val) =>
                      setSplitModal((prev) =>
                        prev
                          ? {
                              ...prev,
                              split: { ...prev.split, [opt.productId]: val ?? 0 },
                            }
                          : null
                      )
                    }
                  />
                </div>
              ))}
              <Text type="secondary">
                Sum:{' '}
                {Object.values(splitModal.split).reduce((a, b) => a + (Number(b) || 0), 0)} / {splitModal.totalQty}
              </Text>
            </Space>
          )}
        </Modal>

        <Modal
          className="deck-price-modal-mobile"
          title={
            variantModal ? (
              <>
                Switch variant: <Text strong>{variantModal.cardName}</Text>
              </>
            ) : null
          }
          open={!!variantModal}
          onCancel={() => setVariantModal(null)}
          footer={null}
          width={Math.min(720, typeof window !== 'undefined' ? window.innerWidth - 48 : 720)}
          destroyOnClose
          styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
        >
          {variantModal && (
            <Row gutter={[16, 16]}>
              {variantModal.options.map((opt) => {
                const isSelected =
                  opt.productId === (selectedProductIds[variantModal.cardKey] ?? variantModal.currentProductId);
                const { url: imageUrlToTry, productIdForError } = getModalOptionImage(opt, variantModal.options);
                const allFailed = variantModal.options.every((o) => failedVariantImages.has(o.productId));

                return (
                  <Col xs={12} sm={12} md={8} key={opt.productId}>
                    <Card
                      size="small"
                      className={`deck-price-variant-option ${isSelected ? 'deck-price-variant-option-selected' : ''}`}
                      hoverable
                      onClick={() => setCardVariant(variantModal.cardKey, opt.productId)}
                    >
                      <div className="deck-price-variant-option-inner">
                        <div className="deck-price-variant-option-image-wrap">
                          {allFailed ? (
                            <a
                              href={opt.productUrl}
                              {...linkProps}
                              className="deck-price-variant-option-placeholder"
                              onClick={(e) => e.stopPropagation()}
                            >
                              No image — View on TCGPlayer
                            </a>
                          ) : (
                            <img
                              src={imageUrlToTry}
                              alt={opt.name}
                              className="deck-price-variant-option-image"
                              referrerPolicy="no-referrer"
                              onError={(e) => {
                                e.stopPropagation();
                                setFailedVariantImages((prev) => new Set(prev).add(productIdForError));
                              }}
                            />
                          )}
                        </div>
                        <div className="deck-price-variant-option-info">
                          <Text type="secondary" className="deck-price-variant-option-set">
                            {opt.setName}
                          </Text>
                          <Text strong className="deck-price-variant-option-price">
                            ${Number(opt.marketPrice).toFixed(2)}
                          </Text>
                          {isSelected && (
                            <Text type="success" style={{ fontSize: 12 }}>
                              Selected
                            </Text>
                          )}
                        </div>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Modal>
      </Space>
    </div>
  );
}
